<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Design system components
	import { Input, Button, Text, Modal, Container } from '@Shelly/shelly-design-system';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { PeripheralInputSettings } from 'src/types/Peripherals';
	import type { Device } from 'src/types/Device';

	let className = '';
	export { className as class };

	export let device: Device, settings: PeripheralInputSettings;

	let step = 0,
		waitForResponse = false;

	const rangeMap = [settings.range_map?.[0] || 0, settings.range_map?.[1] || 100],
		recalibrate = async () => {
			if (step == 0 && rangeMap[0] == 0 && rangeMap[1] == 100) {
				step = 1;
				return;
			}
			waitForResponse = true;
			const range_map = [0, 100];
			const res: SettingRequest = await gen2Request(device, 'input.SetConfig', {
				id: settings.id,
				config: { range_map },
			});
			if (res?.isok) {
				step = step == 0 ? 1 : 0;
				rangeMap[0] = 0;
				rangeMap[1] = 100;
				settings.range_map = rangeMap;
			} else {
				onSettingsResponse(res, device);
			}
			waitForResponse = false;
		},
		getStatus = async () => {
			waitForResponse = true;
			const res: SettingRequest = await gen2Request(device, 'input.GetStatus', {
				id: settings.id,
			});
			if (res?.isok) {
				const percent = res.data.percent;
				if (step == 1) {
					rangeMap[0] = percent;
					step = 2;
				} else if (step == 2) {
					rangeMap[1] = percent;
					settings.range_map = rangeMap;
					step = 0;
				}
			} else {
				onSettingsResponse(res, device);
			}
			waitForResponse = false;
		};
</script>

<div class={className}>
	<Text styleType="body-small" class="mb-s text-left">
		{$format('peripherals_analog_input_calibration_values', {
			default: 'Current calibration values:',
		})}
	</Text>
	<Input
		aria-label={$format('input-gauge-low', {
			default: 'Enter value min value',
		})}
		id="input-gauge-low"
		readonly
		style="pointer-events:none;"
		size="m"
		icon="fas fa-gauge-low"
		class="mb-s"
		value={String(rangeMap[0])}
	/>
	<Input
		aria-label={$format('input-gauge-high', {
			default: 'Enter value max value',
		})}
		id="input-gauge-high"
		readonly
		style="pointer-events:none;"
		size="m"
		icon="fas fa-gauge-high"
		class="mb-s"
		value={String(rangeMap[1])}
	/>
	<Button size="m" width="full" loading={waitForResponse} class="text-uppercase" on:click={recalibrate}
		>{$format('peripherals_analog_input_recalibrate', {
			default: 'Recalibrate',
		})}</Button
	>
</div>
<Modal
	showModal={step == 1}
	on:close={() => {
		step = 0;
	}}
	title={$format('peripherals_analog_input_configure_range_map', {
		default: 'Configure range map',
	})}
>
	<Container slot="content" padding="0">
		<Text
			>{$format('peripherals_analog_input_configure_range_map_minimum_value', {
				default: "Turn the knob of the potentiometer to the minimum value and press 'Next'",
			})}</Text
		>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
			loading={waitForResponse}
			variant={'primary'}
			on:click={getStatus}>{$format('button_next', { default: 'Next' })}</Button
		>
	</div>
</Modal>
<Modal
	showModal={step == 2}
	on:close={() => {
		step = 0;
	}}
	title={$format('peripherals_analog_input_configure_range_map', {
		default: 'Configure range map',
	})}
>
	<Container slot="content" padding="0">
		<Text
			>{$format('peripherals_analog_input_configure_range_map_maximum_value', {
				default: "Turn the knob of the potentiometer to the maximum value and press 'Confirm'",
			})}</Text
		>
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-m width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
			loading={waitForResponse}
			variant={'primary'}
			on:click={getStatus}
			>{capFirstChar($format('button_confirm', { default: 'Confirm' }))}</Button
		>
	</div>
</Modal>
